/**
 * Upload service for handling file uploads to Supabase Storage
 */

import { supabase } from '../supabase';

const BUCKET_NAME = 'community_uploads';

/**
 * Uploads an image file to Supabase Storage
 * Files are stored in user-specific folders: {userId}/{filename}
 * @param file - The file to upload (should be compressed)
 * @param userId - The user ID for folder organization
 * @returns Public URL of the uploaded file
 */
export async function uploadImage(
  file: Blob,
  userId: string,
  originalFileName: string
): Promise<string> {
  // Generate a unique filename with timestamp to avoid collisions
  const timestamp = Date.now();
  const fileExtension = originalFileName.split('.').pop() || 'jpg';
  const fileName = `${timestamp}-${Math.random().toString(36).substring(7)}.${fileExtension}`;
  const filePath = `${userId}/${fileName}`;

  // Upload file to storage
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(filePath, file, {
      contentType: file.type || 'image/jpeg',
      upsert: false, // Don't overwrite existing files
    });

  if (error) {
    throw new Error(`Failed to upload image: ${error.message}`);
  }

  // Get signed URL for the uploaded file (bucket is private)
  // Use a long expiration (10 years) since these are community posts
  const expiresIn = 60 * 60 * 24 * 365 * 10; // 10 years in seconds
  const { data: urlData, error: urlError } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(data.path, expiresIn);

  if (urlError || !urlData?.signedUrl) {
    throw new Error(`Failed to get URL for uploaded image: ${urlError?.message || 'Unknown error'}`);
  }

  return urlData.signedUrl;
}

/**
 * Deletes an image from Supabase Storage
 * @param imageUrl - The signed URL of the image to delete
 * @param userId - The user ID to verify ownership
 */
export async function deleteImage(
  imageUrl: string,
  userId: string
): Promise<void> {
  // Extract the file path from the signed URL
  // URL format: https://{project}.supabase.co/storage/v1/object/sign/{bucket}/{path}?token=...
  const url = new URL(imageUrl);
  const pathMatch = url.pathname.match(/\/storage\/v1\/object\/sign\/[^/]+\/(.+)$/);

  if (!pathMatch) {
    throw new Error('Invalid image URL format');
  }

  const filePath = decodeURIComponent(pathMatch[1]);

  // Verify the file belongs to the user (path should start with userId)
  if (!filePath.startsWith(`${userId}/`)) {
    throw new Error('Unauthorized: Cannot delete image that does not belong to user');
  }

  // Delete the file
  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([filePath]);

  if (error) {
    throw new Error(`Failed to delete image: ${error.message}`);
  }
}

